import { useMemo } from 'react';
import { useSubscription } from 'urql';
import { graphql } from '../lib/graphql';
import { api } from '../lib/api';
import { getErrorMessage } from '../lib/error-message';

const AudioFilesSubscription = graphql(`
  subscription WidgetAudioFiles($organizationId: Uuid!) {
    audioFiles(
      where: { organizationId: { _eq: $organizationId }, deletedAt: { _isNull: true } }
      orderBy: { uploadedAt: DESC }
    ) {
      id
      name
      s3Key
    }
  }
`);

export type AudioFileOption = {
  id: string;
  name: string;
  audioUrl: string;
};

export function useAudioFiles(organizationId: string | null, enabled = true) {
  const [{ data }] = useSubscription({
    query: AudioFilesSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId || !enabled,
  });

  return useMemo<AudioFileOption[] | undefined>(
    () => data?.audioFiles.map((f) => ({ id: f.id, name: f.name, audioUrl: `s3://${f.s3Key}` })),
    [data],
  );
}

export async function uploadAudioFile(organizationId: string, file: File): Promise<AudioFileOption> {
  const contentType = file.type || 'audio/mpeg';

  const signed = await api['audio-files']['upload-url'].post({ organizationId, filename: file.name, contentType });

  if (signed.error) throw new Error(getErrorMessage(signed.error, 'Upload failed'));

  const uploadResponse = await fetch(signed.data.uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  });

  if (!uploadResponse.ok) throw new Error('Upload failed');

  const created = await api['audio-files'].post({
    organizationId,
    name: file.name,
    s3Key: signed.data.s3Key,
    mimeType: contentType,
  });

  if (created.error) throw new Error(getErrorMessage(created.error, 'Upload failed'));

  return { id: created.data.id, name: created.data.name, audioUrl: `s3://${created.data.s3Key}` };
}
